import React, { useEffect, useState } from 'react';
import { FileText, Tag, Bell, ClipboardList, Import } from 'lucide-react';
import { useDbUser } from '../hooks/useDbUser';
import { db, MatterType } from '../lib/db';
import { MatterTypeSettings } from '../components/settings/MatterTypeSettings';
import { NotificationSettings } from '../components/notifications/NotificationSettings';
import { SOPSettings } from '../components/settings/SOPSettings';
import TemplatesSettings from '../components/settings/TemplatesSettings';
import ExportImportSettings from '../components/settings/ExportImportSettings';
import { toast } from 'sonner';

function SettingsPage() {
  const { dbUserId, loading: dbUserLoading } = useDbUser();
  const [matterTypes, setMatterTypes] = useState<MatterType[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'templates' | 'matter-types' | 'notifications' | 'sops' | 'import-export'>('templates');

  useEffect(() => {
    if (dbUserId && !dbUserLoading) {
      loadData();
    }
  }, [dbUserId, dbUserLoading]);

  const loadData = async () => {
    if (!dbUserId) return;
    try {
      setLoading(true);
      const types = await db.matterTypes.findByUserId(dbUserId);
      setMatterTypes(types);
    } catch (error) {
      console.error('Error loading settings data:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  if (dbUserLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
        <p className="text-gray-600">Manage templates, types, notifications, SOPs, and data export/import.</p>
      </div>

      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('templates')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'templates'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FileText className="w-4 h-4 inline-block mr-2" />
            Templates
          </button>
          <button
            onClick={() => setActiveTab('matter-types')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'matter-types'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Tag className="w-4 h-4 inline-block mr-2" />
            Matter Types
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'notifications'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Bell className="w-4 h-4 inline-block mr-2" />
            Notifications
          </button>
          <button
            onClick={() => setActiveTab('sops')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'sops'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <ClipboardList className="w-4 h-4 inline-block mr-2" />
            SOPs
          </button>
          <button
            onClick={() => setActiveTab('import-export')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'import-export'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Import className="w-4 h-4 inline-block mr-2" />
            Import/Export
          </button>
        </nav>
      </div>

      {activeTab === 'matter-types' ? (
        dbUserId && <MatterTypeSettings userId={dbUserId} matterTypes={matterTypes} onUpdate={loadData} />
      ) : activeTab === 'notifications' ? (
        dbUserId && <NotificationSettings userId={dbUserId} onSave={() => toast.success('Notification preferences saved')} />
      ) : activeTab === 'sops' ? (
        dbUserId && <SOPSettings userId={dbUserId} />
      ) : activeTab === 'import-export' ? (
        dbUserId && <ExportImportSettings userId={dbUserId} />
      ) : (
        dbUserId && <TemplatesSettings userId={dbUserId} />
      )}
    </div>
  );
}

export default SettingsPage;

